import { createPortal } from "react-dom";
import { Check, Globe } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useLocation } from "wouter";
import { useAuth } from "@/_core/hooks/useAuth";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { LOCALE_LABELS, SUPPORTED_LOCALES, DEFAULT_LOCALE, type AppLocale } from "@/i18n";
import { useLocale } from "@/i18n/useLocale";
import { LANDING_TOURNAMENT_IDS, wantsClassicTournamentView } from "@/pages/landings/registry";

/** Переключатель языка интерфейса. Вариант для выпадающего меню профиля. */
export function LanguageSwitcherItems() {
  const { locale, setLocale, isPending } = useLocale();
  return (
    <>
      {SUPPORTED_LOCALES.map((code) => (
        <DropdownMenuItem
          key={code}
          onClick={() => setLocale(code)}
          disabled={isPending}
          className="cursor-pointer"
        >
          <Globe className="mr-2 h-4 w-4" />
          <span className="flex-1">{LOCALE_LABELS[code]}</span>
          {locale === code && <Check className="ml-2 h-4 w-4 text-primary" />}
        </DropdownMenuItem>
      ))}
    </>
  );
}

/**
 * Переключатель языка для планшета судьи (/j/). Судьи заходят по коду доступа и
 * НЕ авторизованы, поэтому меняем только i18n локально (кэшируется в localStorage
 * этого устройства) — без мутации профиля. Каждый судья выбирает язык на своём
 * планшете. Дискретная угловая кнопка-глобус.
 */
export function TabletLanguageSwitcher({ className = "" }: { className?: string }) {
  const { i18n } = useTranslation();
  const current = ((i18n.resolvedLanguage || i18n.language || DEFAULT_LOCALE) as string).slice(0, 2) as AppLocale;
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          aria-label="Язык / Language"
          className={`inline-flex items-center gap-1.5 px-2.5 py-2 rounded-full bg-card/85 backdrop-blur-md border border-border text-foreground/80 text-xs font-medium hover:bg-card hover:text-foreground transition-colors ${className}`}
        >
          <Globe className="h-4 w-4" />
          <span className="uppercase">{current}</span>
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-40">
        {SUPPORTED_LOCALES.map((code) => (
          <DropdownMenuItem
            key={code}
            onClick={() => void i18n.changeLanguage(code)}
            className="cursor-pointer"
          >
            <span className="flex-1">{LOCALE_LABELS[code]}</span>
            {current === code && <Check className="ml-2 h-4 w-4 text-primary" />}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

/**
 * Самостоятельный переключатель (кнопка-глобус) для верхней панели и публичных
 * экранов. По умолчанию сохраняет язык в профиль (useLocale). С `localOnly`
 * (Login/Register, до авторизации) меняет только i18n локально — без обращения
 * к серверу, поэтому без ошибок 401.
 */
export function LanguageSwitcher({ localOnly = false, className = "" }: { localOnly?: boolean; className?: string }) {
  const { i18n } = useTranslation();
  const { locale, setLocale, isPending } = useLocale();
  const current = localOnly
    ? (((i18n.resolvedLanguage || i18n.language || DEFAULT_LOCALE) as string).slice(0, 2) as AppLocale)
    : locale;
  const change = (code: AppLocale) => (localOnly ? void i18n.changeLanguage(code) : setLocale(code));
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          aria-label="Язык интерфейса / Interface language"
          className={`inline-flex items-center gap-1.5 h-9 px-2.5 rounded-lg text-sm hover:bg-accent/50 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-ring ${className}`}
        >
          <Globe className="h-4 w-4" />
          <span className="uppercase">{current}</span>
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-44">
        {SUPPORTED_LOCALES.map((code) => (
          <DropdownMenuItem
            key={code}
            onClick={() => change(code)}
            disabled={!localOnly && isPending}
            className="cursor-pointer"
          >
            <span className="flex-1">{LOCALE_LABELS[code]}</span>
            {current === code && <Check className="ml-2 h-4 w-4 text-primary" />}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

// Публичные маршруты (без DashboardLayout и без собственного переключателя),
// где нужен глобальный переключатель языка. Кабинеты, /login, /register,
// главная, судейский планшет и трансляционные экраны уже имеют свой (или он
// им не нужен), поэтому их здесь нет — чтобы не было дублирования.
const PUBLIC_SWITCHER_PREFIXES = [
  "/privacy",
  "/forgot-password",
  "/reset-password",
  "/tournaments",
  "/tournament",
  "/live",
  "/schedule",
  "/support",
  "/knowledge",
  "/accept-invite",
  "/accept-coach-invite",
  "/demo",
  "/404",
];

/**
 * Глобальный переключатель языка для публичных страниц. Монтируется один раз
 * в App и сам решает, показываться ли (по маршруту). Для неавторизованных
 * посетителей меняет язык только локально (localOnly) — без 401.
 */
export function GlobalLanguageSwitcher() {
  const [location] = useLocation();
  const { isAuthenticated } = useAuth();
  const show = PUBLIC_SWITCHER_PREFIXES.some(
    (p) => location === p || location.startsWith(`${p}/`),
  );
  // Дизайнерские лендинги несут собственный переключатель в шапке — плавающая
  // пилюля их перекрывала. Классический вид (?view=classic) — обычная страница.
  const landingMatch = location.match(/^\/tournament\/(\d+)$/);
  const isLanding = !!landingMatch
    && LANDING_TOURNAMENT_IDS.has(Number(landingMatch[1]))
    && !wantsClassicTournamentView();
  if (!show || isLanding) return null;
  // Портал в body: #root режет горизонтальный overflow (см. index.css), а
  // Safari при этом прячет fixed-элементы внутри него при прокрутке.
  return createPortal(
    <LanguageSwitcher
      localOnly={!isAuthenticated}
      className="global-lang-switcher fixed top-3 right-3 z-50 bg-background/70 backdrop-blur border border-border/60 shadow-sm"
    />,
    document.body,
  );
}
